import '../../i18n/autoTranslate';
import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import SpotlightCard from '../../../components/ui/SpotlightCard';

const SessionFormatSelector = ({
  embedded = false,
  selectedFormat,
  onFormatChange,
  duration=15,
  onDurationChange,
  className = '',
}) => {
  const formats = [
    {
      id: 'phone',
      name: 'Phone Interview',
      description: 'Audio-only simulation focusing on verbal communication',
      icon: '/phone_prnv4s.png',
      features: ['Voice recording', 'Audio feedback', 'Speaking pace analysis'],
      recommended: false,
    },
    {
      id: 'video',
      name: 'Video Interview',
      description: 'Full video simulation with visual and audio analysis',
      icon: '/webcam_u0fdko.png',
      features: ['Video recording', 'Body language analysis', 'Eye contact tracking'],
      recommended: false,
      comingSoon: true,
    },
    {
      id: 'chat',
      name: 'Chat Interview',
      description: 'Text-based simulation for quick practice and async feedback',
      icon: '/chat_acgx93.png',
      features: [
        'Text-based Q&A',
        'AI-driven instant feedback',
        'Answer history & review',
        'Typing speed & clarity analysis',
      ],
      recommended: false,
    },
  ];

  const durationOptions = [
    { value: 15, label: '15 minutes', description: 'Quick practice session' },
    { value: 30, label: '30 minutes', description: 'Standard interview length' },
    { value: 45, label: '45 minutes', description: 'Extended practice session' },
    { value: 60, label: '60 minutes', description: 'Full interview simulation' },
  ];

  const answerModes = [
    {
      id: 'text',
      name: 'Text Responses',
      description: 'Type your answers for detailed analysis',
      icon: 'Type',
      available: true,
    },
    {
      id: 'audio',
      name: 'Voice Responses',
      description: 'Speak your answers for natural practice',
      icon: 'Mic',
      available: selectedFormat !== 'text_only',
    },
    {
      id: 'video',
      name: 'Video Responses',
      description: 'Record video answers for complete feedback',
      icon: 'Camera',
      available: selectedFormat === 'video' || selectedFormat === 'in_person',
    },
    {
      id: 'presentation',
      name: 'Presentation Mode',
      description: 'Present slides or use whiteboard',
      icon: 'Presentation',
      available: selectedFormat === 'in_person',
    },
  ];

  const panel =
    'relative overflow-hidden rounded-3xl border border-white/10 bg-[#0b0f16]/60 backdrop-blur-xl shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_18px_60px_-40px_rgba(56,189,248,0.18)]';

  const headIcon =
    'flex items-center justify-center w-9 h-9 rounded-2xl border border-white/10 bg-gradient-to-br from-sky-500/15 via-violet-500/10 to-emerald-500/10 shadow-[0_0_22px_rgba(56,189,248,0.20)]';

  return (
    <div className={`transition-all duration-200 ease-smooth space-y-8 ${className}`}>
      {!embedded && (
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center gap-3 mb-2">
            <div className="flex items-center justify-center w-10 h-10 rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl">
              <img src="/screen_btq7sx.png" alt="screen" className="w-10 h-10" />
            </div>
            <h2 className="text-2xl font-semibold tracking-tight text-white">Session Format</h2>
          </div>
          <p className="text-white/60">Choose how you want to practice your interview.</p>
        </div>
      )}

      {/* Format Selection */}
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div className={headIcon}>
            <Icon name="Monitor" size={16} className="text-white" />
          </div>
          <h3 className="text-lg font-semibold text-white">Interview Format</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-fr">
          {formats?.map((format) => {
            const isSelected = selectedFormat === format?.id;
            const isDisabled = Boolean(format?.comingSoon);

            return (
              <motion.button
                key={format?.id}
                type="button"
                onClick={isDisabled ? undefined : () => onFormatChange(format?.id)}
                disabled={isDisabled}
                whileHover={isDisabled ? undefined : { y: -3 }}
                whileTap={isDisabled ? undefined : { scale: 0.99 }}
                transition={{ type: 'spring', stiffness: 260, damping: 18 }}
                className={`group h-full text-left ${isDisabled ? 'cursor-not-allowed' : ''}`}
              >
                <SpotlightCard
                  className="custom-spotlight-card h-full rounded-3xl overflow-hidden"
                  spotlightColor={isSelected ? 'rgba(56,189,248,0.26)' : 'rgba(255,255,255,0.06)'}
                >
                  <div
                    className={`relative h-full rounded-3xl border transition-all duration-300 overflow-hidden p-6 backdrop-blur-xl
                      ${
                        isSelected
                          ? 'border-white/18 bg-gradient-to-br from-sky-500/18 via-violet-500/10 to-emerald-500/10 shadow-[0_0_0_1px_rgba(255,255,255,0.05),0_18px_60px_-40px_rgba(56,189,248,0.55)]'
                          : 'border-white/10 bg-[#0b0f16]/60 hover:border-white/16 hover:bg-white/[0.04]'
                      }
                      ${isDisabled ? 'opacity-60 hover:border-white/10 hover:bg-[#0b0f16]/60' : ''}
                    `}
                  >
                    <div className="absolute inset-0 opacity-60 pointer-events-none [background:radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.10),transparent_60%)]" />

                    {format?.recommended && (
                      <div className="absolute top-4 right-4 px-2.5 py-1 rounded-full text-[11px] font-medium border border-white/12 bg-white/[0.05] text-white/85">
                        Recommended
                      </div>
                    )}
                    {format?.comingSoon && (
                      <div className="absolute top-4 right-4 px-2.5 py-1 rounded-full text-[11px] font-medium border border-white/12 bg-white/[0.05] text-white/75">
                        Coming Soon
                      </div>
                    )}

                    <div className="relative flex items-center justify-center">
                      <div
                        className={`mt-1 flex h-16 w-16 items-center justify-center rounded-2xl border transition-transform duration-300 group-hover:scale-[1.04]
                          ${
                            isSelected
                              ? 'border-white/18 bg-white/[0.06]'
                              : 'border-white/10 bg-white/[0.03]'
                          }
                        `}
                      >
                        <img src={format?.icon} alt={format?.name} className="h-10 w-10 object-contain" />
                      </div>
                    </div>

                    <div className="relative mt-4 space-y-3">
                      <h4 className="text-xl font-semibold tracking-tight text-white">{format?.name}</h4>
                      <p className="text-sm text-white/70 leading-relaxed">{format?.description}</p>

                      <div className="flex flex-wrap gap-2 pt-1">
                        {format?.features?.slice(0, 3)?.map((feature, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2.5 py-1 rounded-full text-xs bg-white/[0.03] text-white/70 border border-white/10"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </SpotlightCard>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Duration Selection */}
      {/* <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div className={headIcon}>
            <Icon name="Clock" size={16} className="text-white" />
          </div>
          <h3 className="text-lg font-semibold text-white">Session Duration</h3>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {durationOptions?.map((option) => {
            const isSelected = duration === option?.value;
            return (
              <motion.button
                key={option?.value}
                type="button"
                onClick={() => onDurationChange(option?.value)}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.99 }}
                transition={{ type: 'spring', stiffness: 260, damping: 18 }}
                className="group"
              >
                <SpotlightCard
                  className="custom-spotlight-card h-full rounded-3xl overflow-hidden"
                  spotlightColor={isSelected ? 'rgba(16,185,129,0.22)' : 'rgba(255,255,255,0.06)'}
                >
                  <div
                    className={`relative h-full rounded-3xl border transition-all duration-300 text-center overflow-hidden p-4 flex flex-col justify-center backdrop-blur-xl
                      ${
                        isSelected
                          ? 'border-white/18 bg-gradient-to-br from-emerald-500/18 via-sky-500/10 to-violet-500/10 shadow-[0_0_0_1px_rgba(255,255,255,0.05),0_18px_60px_-40px_rgba(16,185,129,0.55)]'
                          : 'border-white/10 bg-[#0b0f16]/60 hover:border-white/16 hover:bg-white/[0.04]'
                      }
                    `}
                  >
                    <div className="absolute inset-0 opacity-60 pointer-events-none [background:radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.10),transparent_60%)]" />
                    <div className="relative text-2xl font-bold mb-1 text-white">{option?.value}</div>
                    <div className="relative text-xs text-white/65 mb-1">minutes</div>
                    <div className="relative text-xs text-white/55">{option?.description}</div>
                  </div>
                </SpotlightCard>
              </motion.button>
            );
          })}
        </div>
      </div> */}

      {/* Answer Modes */}
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div className={headIcon}>
            <Icon name="MessageSquare" size={16} className="text-white" />
          </div>
          <h3 className="text-lg font-semibold text-white">Answer Input Methods</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {answerModes?.map((mode) => (
            <div
              key={mode?.id}
              className={`${panel} p-5 transition-all duration-300 ${
                mode?.available
                  ? 'hover:border-white/16 hover:bg-white/[0.04]'
                  : 'opacity-50'
              }`}
            >
              <div className="pointer-events-none absolute inset-0 opacity-60 [background:radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.08),transparent_60%)]" />
              <div className="relative flex items-start gap-3">
                <div
                  className={`w-10 h-10 rounded-2xl flex items-center justify-center border transition-colors duration-200 backdrop-blur-xl
                    ${
                      mode?.available
                        ? 'bg-gradient-to-br from-sky-500/15 via-violet-500/10 to-emerald-500/10 border-white/10 text-white'
                        : 'bg-white/[0.03] border-white/10 text-white/40'
                    }
                  `}
                >
                  <Icon name={mode?.icon} size={18} />
                </div>

                <div className="flex-1">
                  <h4 className={`font-medium mb-1 ${mode?.available ? 'text-white' : 'text-white/45'}`}>
                    {mode?.name}
                  </h4>
                  <p className={`text-sm ${mode?.available ? 'text-white/70' : 'text-white/45'}`}>
                    {mode?.description}
                  </p>
                </div>

                {mode?.available && <Icon name="Check" size={16} className="text-emerald-300" />}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Summary Section */}
      <div className="pt-6 border-t border-white/10">
        <div className={`${panel} p-5`}>
          <div className="pointer-events-none absolute inset-0 opacity-60 [background:radial-gradient(circle_at_20%_20%,rgba(56,189,248,0.16),transparent_55%)]" />
          <div className="relative flex items-start gap-3">
            <div className={headIcon}>
              <Icon name="Info" size={16} className="text-white" />
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-medium text-white mb-2">Session Configuration</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-white/60">
                <div className="flex items-start gap-2">
                  <Icon name="Monitor" size={12} className="text-white mt-0.5 flex-shrink-0" />
                  <span>
                    Format: {formats.find((f) => f.id === selectedFormat)?.name || 'None selected'}
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <Icon name="Clock" size={12} className="text-white mt-0.5 flex-shrink-0" />
                  <span>Duration: {duration ? `${duration} minutes` : 'Not selected'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionFormatSelector;
